import React, { Fragment, useCallback, useEffect, useState } from "react";
import DefaultButton from "../../components/buttons/defaultButton";
import PageTableHeader from "../../components/pageTableHeader/pageTableHeader";
import DefaultBadge from "../../components/badge/defaultBadge";
import { Link, useNavigate } from "react-router-dom";
import { getReminderList, updateReminder } from "../../../services/workspace/reminder.service";
import { useSelector } from "react-redux";
import { _categorizeByDate, formatTime } from "../../../utils/common";
import toast from "react-hot-toast";
import moment from "moment";
import ListSkeleton from "../../components/skeleton/listSheleton";
import { LuAlarmClockMinus, LuAlarmClockOff } from "react-icons/lu";
import ConfirmModal from "../../components/modals/ConfirmModel";
import { IoMdAdd } from "react-icons/io";

const breadcrumbs = [
  { label: "Home", path: "/" },
  { label: "Reminder", path: "" },
];

const reminderType = {
  1: "Scheduled",
  0: "Dismissed",
  2: "Expired",
};

const Reminder = () => {
  const navigate = useNavigate();
  const { limit } = useSelector((state) => state.global);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchMore, setfetchMore] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(false);

  const fetchData = useCallback(() => {
    if (currentPage == 0 || !fetchMore) {
      setLoading(true);
    }
    getReminderList({ search: "", page: currentPage, limit })
      .then((response) => {
        setTableData(prevTableData => {
          const combinedData = currentPage === 1 ? [...response.data] : [...prevTableData, ...response.data];
          return combinedData;
        });
        setTotalData(response.total);
      })
      .catch((error) => {
        toast.error("Failed to fetch reminder data!");
        console.error("Error fetching data: ", error);
      }).finally(() => {
        setLoading(false);
        setfetchMore(false)
      })
  }, [currentPage, limit]);

  useEffect(() => {
    fetchData()
  }, [fetchData]);;

  const fetchMoreData = () => {
    if (totalData == tableData?.length) return
    setfetchMore(true);
    setCurrentPage((prev) => prev + 1)
  }

  const onConfirm = () => {
    const payload = { status: 0 }
    updateReminder(selectedId, payload)
      .then((response) => {
        fetchData();
        toast.success("Reminder Dismiss successfully!");
      })
      .catch((error) => {
        toast.error("Failed to dismiss reminder." + error.response.data?.message);
      });
    setIsOpen(false);
  };

  const handleDismiss = (id) => {
    setSelectedId(id);
    setIsOpen(true);
  };

  return (
    <div className="flex flex-col gap-2 p-4">
      <PageTableHeader
        breadcrumbs={breadcrumbs}
        isSearch={false}
        isFilter={false}
        buttons={
          <DefaultButton
            name="Add"
            icon={<IoMdAdd />}
            onClick={() => {
              navigate("/reminder/add");
            }}
          />
        }
      />
      <div className="flex flex-col gap-4 bg-white p-4">
        {loading ?
          <ListSkeleton />
          :
          tableData && tableData.length ?
            <Fragment>
              {_categorizeByDate(tableData).map((item) => {
                return (
                  <div className="flex flex-col gap-1">
                    <p className="text-textLight">{item.category || "-"}</p>
                    <div className="flex flex-col gap-2">
                      {item.data.map((alert, index) => (
                        <ReminderCard handleDismiss={handleDismiss} key={index} {...alert} />
                      ))}
                    </div>
                  </div>
                )
              })}
              {totalData === tableData.length ? null :
                <div className="flexCenter">
                  <DefaultButton
                    loading={fetchMore}
                    disabled={fetchMore || totalData === tableData.length}
                    onClick={fetchMoreData}
                    name={"Load More"}
                  />
                </div>}
            </Fragment> :
            <div className="flexCenter">
              No Data Found!
            </div>
        }

        <ConfirmModal
          isOpen={isOpen}
          title="Attention!"
          message="Are you sure you want to Dismiss/Cancel this Reminder?"
          onConfirm={onConfirm}
          onCancel={() => setIsOpen(false)}
        />
      </div>
    </div>
  );
};

const ReminderCard = ({ handleDismiss = () => null,
  tag, title, link, created_at, date, time, description, status, ...rest
}) => (
  <Link target={link ? "_blank" : null} to={link ? link : "javascript:void(0)"} className={`bg-light hover:bg-bghoverlight cursor-pointer p-4 rounded-lg
   grid grid-cols-12 items-center gap-y-2`}>
    <div className="col-span-12 md:col-span-2 flex h-full items-center gap-4">
      <div className="flex md:flex-col items-center gap-1">
        <span className="text-textLight font-medium text-sm">
          {moment(date).format("ddd").toUpperCase()}
        </span>
        <span className="text-primary-900 font-medium md:text-2xl">
          {moment(date).format("DD")}
        </span>
      </div>
      <div className="w-[2px] h-full bg-bghoverlight" />
      <span className="text-textLight">
        <span className="text-xl"><LuAlarmClockMinus /></span>
        {formatTime(time)}
      </span>
    </div>

    <div className="flex flex-col text-theme col-span-12 md:col-span-7">
      <div className="font-semibold flex items-center gap-2">
        <h2>{title}</h2>
        <DefaultBadge customCases={reminderType} status={status} />
      </div>
      <span className="text-textLight">{description}</span>
    </div>

    <div className="flex md:flex-col gap-2 justify-between md:justify-start md:items-end col-span-12 md:col-span-3">
      <div className="text-textLight flex gap-1">
        <span className="flex gap-1">{moment(created_at).format("YYYY-MM-DD")} | {moment(created_at).format("hh:mm a")}</span>
      </div>

      {status == 1 ?
        <DefaultButton
          tooltip="Dismiss"
          onClick={() => handleDismiss(rest.uuid)}
          icon={<LuAlarmClockOff />}
        />
        :
        null
      }
    </div>
  </Link>
);

export default Reminder;
